import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useScroll,
  useMotionValueEvent,
} from "framer-motion";
import { Menu, X, ArrowUpRight, ArrowRight } from "lucide-react";
import { services } from "../data/services";
import { Logo } from "./Logo";
import { useHeaderHidden } from "./useHeaderHidden";

const links = [
  { label: "Home", to: "/" },
  { label: "About", to: "/#about" },
  { label: "Services", to: "/services", mega: true },
  { label: "Project", to: "/project" },
  { label: "Career", to: "/career" },
];

export const Header = () => {
  const location = useLocation();
  const headerHidden = useHeaderHidden();
  const { scrollY } = useScroll();
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const hidden = headerHidden && !mobileOpen;

  useMotionValueEvent(scrollY, "change", (y) => {
    setScrolled(y > 40);
  });

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <>
      <motion.header
        data-testid="site-header"
        initial={false}
        animate={{ y: hidden ? "-100%" : "0%" }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
          scrolled
            ? "border-b border-white/10 bg-[#07130C]/75 shadow-[0_10px_40px_rgba(7,19,12,0.35)] backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-8 md:px-14">
          <Link
            to="/"
            data-testid="header-logo"
            aria-label="Social Wavez Technologies — home"
            className="flex items-center gap-3 transition-transform duration-300 hover:scale-[1.04]"
          >
            <img
              src="/assets/logo-mark.png"
              alt="Social Wavez logo"
              className="h-11 w-11 shrink-0 object-contain"
            />

            <span className="flex flex-col leading-none">
              <span
                className="bg-gradient-to-r from-[#3CBE6E] via-[#8FAF4E] to-[#E3C65F] bg-clip-text font-display text-transparent"
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                }}
              >
                SOCIAL WAVEZ
              </span>

              <span
                className="mt-1 font-medium uppercase text-white"
                style={{
                  fontSize: 8,
                  letterSpacing: "0.32em",
                }}
              >
                TECHNOLOGIES
              </span>
            </span>
          </Link>

          <nav
            className="relative hidden items-center gap-9 lg:flex"
            aria-label="Primary"
          >
            {links.map((link) =>
              link.mega ? (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setMegaOpen(true)}
                  onMouseLeave={() => setMegaOpen(false)}
                >
                  <NavLink
                    to={link.to}
                    data-testid="nav-services"
                    onFocus={() => setMegaOpen(true)}
                    className={`py-2 text-sm font-medium transition-colors duration-200 ${
                      isActive(link.to)
                        ? "text-white"
                        : "text-white/60 hover:text-white"
                    }`}
                  >
                    {link.label}
                  </NavLink>

                  {/* Mega Menu - Positioned directly under Services */}
                  <AnimatePresence>
                    {megaOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="absolute top-full left-0 z-[9999] pt-4"
                        style={{ 
                          width: "680px",
                          marginLeft: "-300px", // Adjust this to center under Services
                        }}
                      >
                        <div
                          data-testid="services-mega-menu"
                          className="grid grid-cols-2 gap-1 rounded-3xl border border-white/10 bg-[#0B2A1E]/95 p-3 shadow-[0_30px_80px_rgba(7,19,12,0.6)] backdrop-blur-2xl"
                        >
                          {services.map((s) => (
                            <Link
                              key={s.id}
                              to={`/services#${s.id}`}
                              data-testid={`mega-menu-${s.id}`}
                              onClick={() => setMegaOpen(false)}
                              className="group flex items-start gap-4 rounded-2xl p-4 transition-colors duration-200 hover:bg-white/5"
                            >
                              <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#1C7543]/25 to-[#D3B74D]/25 text-[#D3B74D] transition-transform duration-300 group-hover:scale-110">
                                <s.icon className="h-5 w-5" />
                              </span>

                              <span>
                                <span className="flex items-center gap-1.5 text-sm font-semibold text-white">
                                  {s.title}

                                  <ArrowUpRight className="h-3.5 w-3.5 text-[#D3B74D] opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
                                </span>

                                <span className="mt-1 block text-xs leading-relaxed text-white/50">
                                  {s.menuDesc}
                                </span>
                              </span>
                            </Link>
                          ))}

                          <Link
                            to="/contact"
                            data-testid="mega-menu-cta"
                            onClick={() => setMegaOpen(false)}
                            className="group col-span-2 flex items-center justify-between rounded-2xl bg-gradient-to-r from-[#1C7543]/20 to-[#B8912A]/20 p-4 transition-colors duration-200 hover:from-[#1C7543]/35 hover:to-[#B8912A]/35"
                          >
                            <span className="text-sm font-semibold text-white">
                              Not sure what you need?
                            </span>

                            <span className="flex items-center gap-1 text-xs font-semibold text-[#B8912A]">
                              Book a free call

                              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                            </span>
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <NavLink
                  key={link.label}
                  to={link.to}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                  className={`py-2 text-sm font-medium transition-colors duration-200 ${
                    isActive(link.to)
                      ? "text-white"
                      : "text-white/60 hover:text-white"
                  }`}
                >
                  {link.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="header-contact-cta"
              className="group relative hidden overflow-hidden rounded-full bg-gradient-to-r from-[#1C7543] to-[#D3B74D] px-6 py-2.5 text-sm font-semibold text-white shadow-[0_8px_28px_rgba(28,117,67,0.4)] transition-shadow duration-300 hover:shadow-[0_8px_36px_rgba(211,183,77,0.55)] lg:inline-flex"
            >
              <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
              <span className="relative">Contact Us</span>
            </Link>
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white lg:hidden"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#07130C]/98 backdrop-blur-2xl lg:hidden"
          >
            <div className="flex h-full flex-col justify-center gap-2 px-8">
              {[...links, { label: "Contact Us", to: "/contact" }].map(
                (link, i) => (
                  <motion.div
                    key={link.label}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.07, duration: 0.4 }}
                  >
                    <Link
                      to={link.to}
                      data-testid={`mobile-nav-${link.label
                        .toLowerCase()
                        .replace(/\s/g, "-")}`}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center justify-between border-b border-white/10 py-5 font-display text-3xl font-semibold text-white"
                    >
                      {link.label}
                      <ArrowUpRight className="h-6 w-6 text-[#D3B74D]" />
                    </Link>
                  </motion.div>
                )
              )}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mt-8 text-xs uppercase tracking-[0.3em] text-white/40"
              >
              We build the systems your business runs on
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};